import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { ProgressionStore } from '@/features/jobs/taxes/progression/ProgressionStore'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import type { ProgressionStepStatus } from '@/features/jobs/taxes/progression/JobProgression'
import { toStream } from '@/utils/mobx-util'
import { filter, tap } from 'rxjs'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { GetProgressionFn } from './ProgressionViewModel'
import {
  computeProgressionFlow,
  getComputedExtensionCurrentStepContent,
  isExtensionFlow,
} from '@/features/jobs/taxes/progression/progression-content'

/**
 * View model for extension-related functionality.
 */
export class ExtensionViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Modal for displaying ready to file confirmation.
   */
  readonly confirmReadyToFileModalVm = new ModalViewModel()

  constructor(
    public readonly job: SubmittedJob,
    private readonly _getProgression: GetProgressionFn,
    private readonly _progressionStore: ProgressionStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
  ) {
    // Close ready to file modals when we're not in the ready to file step
    this._lifecycle.manage(
      toStream(() => _getProgression()?.current, true).pipe(
        filter((s) => s !== 'ReadyToFile'),
        tap(this._closeReadyToFileModals),
      ),
    )
  }

  /**
   * Checks if the job is an extension-only job.
   */
  @computed
  get isExtensionOnlyJob() {
    return this.job.match({
      tax: (tax) => tax.isExtensionOnlyJob,
      default: () => false,
    })
  }

  /**
   * Checks if the current progression flow is an extension flow.
   */
  @computed
  get isExtensionFlow() {
    const flow = this._getProgression()?.flow
    if (!flow) {
      return false
    }

    return isExtensionFlow(flow)
  }

  /**
   * The content for the current step in the progression flow, with
   * extension-related overrides.
   */
  @computed
  get currentStepContent() {
    const progression = this._getProgression()

    if (!progression) {
      return
    }

    return getComputedExtensionCurrentStepContent(
      progression?.current,
      this.allSteps,
    )
  }

  /**
   * All steps in the current progression flow, with extension-related overrides.
   */
  @computed
  get allSteps() {
    const progression = this._getProgression()

    if (!progression) {
      return []
    }

    return computeProgressionFlow({
      flow: progression.flow,
      steps: progression?.recordedSteps.items.map((s) => s.step) ?? [],
      isExtensionOnlyJob: this.isExtensionOnlyJob,
    })
  }

  /**
   * Determines if the given step is extension-related.
   */
  isExtensionStep(step: ProgressionStepStatus) {
    switch (step) {
      case 'ExtensionInProgress':
      case 'ExtensionConfirmed':
      case 'ExtensionAccepted':
      case 'ExtensionRejected':
        return true
      default:
        return false
    }
  }

  /**
   * Checks if the extension has been accepted.
   */
  isExtensionAccepted(recordedSteps: ProgressionStepStatus[]) {
    return recordedSteps.includes('ExtensionAccepted')
  }

  /**
   * Checks if the extension has been rejected.
   */
  isExtensionRejected(recordedSteps: ProgressionStepStatus[]) {
    return recordedSteps.includes('ExtensionRejected')
  }

  /**
   * Shows the ready to file confirmation dialog.
   */
  @action.bound
  readyToFile() {
    this.confirmReadyToFileModalVm.show()
  }

  /**
   * Confirms that the user is ready to file.
   */
  confirmReadyToFile = task.resolved(async () => {
    await this._progressionStore
      .transitionToStep(this.job.id, 'ReadyToFile')
      .catch(
        this._createSnackbarErrorHandler('Unable to continue with the job'),
      )
    this._closeReadyToFileModals()
  })

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._closeReadyToFileModals()
    this._lifecycle.deactivate()
  }

  /**
   * Closes all ready to file related modals.
   * @private
   */
  @action.bound
  private _closeReadyToFileModals() {
    this.confirmReadyToFileModalVm.close()
  }
}
